import React, { useState } from 'react';
import { Star, X, Send, Loader2 } from 'lucide-react';
import { ratingService } from '../../../services/ratingService';

export default function RatingModal({ negotiation, currentUserId, onClose, onRated }) {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const counterpartyName = negotiation?.counterpartyName || 'Korisnik';

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Molimo izaberite ocenu (1-5 zvezda)');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await ratingService?.submitRating(
        negotiation?.id,
        negotiation?.counterpartyId,
        rating,
        comment
      );
      onRated?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Greška pri slanju ocene');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={18} className="text-gray-500" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Star size={28} className="text-teal-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Ocenite saradnju</h2>
          <p className="text-sm text-gray-500 mt-1">
            Kako je prošla saradnja sa <strong>{counterpartyName}</strong>?
          </p>
        </div>

        {/* Stars */}
        <div className="flex justify-center gap-2 mb-5">
          {[1, 2, 3, 4, 5]?.map(star => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              className="transition-transform hover:scale-110"
            >
              <Star
                size={36}
                className={`transition-colors ${
                  star <= (hovered || rating)
                    ? 'fill-current' :'text-gray-300'
                }`}
                style={star <= (hovered || rating) ? {color:'#CFB53B'} : {}}
              />
            </button>
          ))}
        </div>

        {/* Rating label */}
        {(hovered || rating) > 0 && (
          <p className="text-center text-sm font-medium mb-4" style={{color:'#CFB53B'}}>
            {['', 'Loše', 'Ispod proseka', 'Prosečno', 'Dobro', 'Odlično']?.[hovered || rating]}
          </p>
        )}

        {/* Comment */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Komentar <span className="text-gray-400 font-normal">(opciono)</span>
          </label>
          <textarea
            value={comment}
            onChange={e => setComment(e?.target?.value)}
            placeholder="Podelite iskustvo saradnje..."
            rows={3}
            maxLength={500}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
          />
          <p className="text-xs text-gray-400 text-right mt-1">{comment?.length}/500</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
          >
            Preskoči
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-teal-700 text-white text-sm font-medium rounded-xl hover:bg-teal-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            Pošalji ocenu
          </button>
        </div>
      </div>
    </div>
  );
}
